// apps/workflow/src/pages/designer/Submissions.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  FileText,
  Download,
  Eye,
  RefreshCw,
  Search,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

// ─── Helpers ──────────────────────────────────────────────────────
const getStatusBadge = (status) => {
  const config = {
    'pending-review': { label: 'Pending Review', color: 'bg-amber-100 text-amber-800' },
    approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
    'revision-requested': { label: 'Revision Requested', color: 'bg-red-100 text-red-800' },
  };
  return config[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-800' };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

// ─── Main Component ──────────────────────────────────────────────
const DesignerSubmissions = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchSubmissions = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const records = await pb.collection('task_submissions').getFullList({
        filter: `designer = "${user.id}"`,
        sort: '-created',
        expand: 'task, task.project',
        requestKey: null,
      });
      setSubmissions(records);
    } catch (error) {
      if (!error.isAbort) {
        console.error('Error fetching submissions:', error);
        toast.error('Failed to load submissions');
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSubmissions();
    setRefreshing(false);
    toast.success('Submissions refreshed');
  };

  const handleDownload = (submission) => {
    const url = pb.files.getUrl(submission, submission.file);
    window.open(url, '_blank');
  };

  // ─── Filtering ──────────────────────────────────────────────
  const filteredSubmissions = submissions.filter((s) => {
    const matchesSearch =
      s.expand?.task?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.expand?.task?.expand?.project?.projectName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || s.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // ─── Stats ──────────────────────────────────────────────────
  const stats = {
    total: submissions.length,
    pending: submissions.filter((s) => s.status === 'pending-review').length,
    approved: submissions.filter((s) => s.status === 'approved').length,
    revision: submissions.filter((s) => s.status === 'revision-requested').length,
  };

  // ─── Loading ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading submissions...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-6 h-6 text-indigo-600" />
            My Submissions
          </h1>
          <p className="text-sm text-slate-500">All designs you have submitted</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 text-slate-400 hover:text-indigo-600 transition rounded-lg hover:bg-indigo-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard title="Total" value={stats.total} icon={FileText} color="indigo" />
        <StatCard title="Pending Review" value={stats.pending} icon={Clock} color="amber" />
        <StatCard title="Approved" value={stats.approved} icon={CheckCircle} color="green" />
        <StatCard title="Revision Requested" value={stats.revision} icon={AlertCircle} color="red" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-3 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-1 min-w-[150px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by task or project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
        >
          <option value="all">All Status</option>
          <option value="pending-review">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="revision-requested">Revision Requested</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {filteredSubmissions.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">No submissions found.</p>
            <p className="text-xs text-slate-400 mt-1">
              Submit designs from task details page.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Task
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Submitted On
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredSubmissions.map((sub) => {
                  const status = getStatusBadge(sub.status);
                  const task = sub.expand?.task;
                  const project = task?.expand?.project;
                  return (
                    <tr key={sub.id} className="hover:bg-slate-50/50 transition">
                      <td className="px-6 py-3 font-medium text-slate-800">
                        {task?.title || 'Deleted Task'}
                      </td>
                      <td className="px-6 py-3 text-slate-600">
                        {project?.projectName || 'N/A'}
                      </td>
                      <td className="px-6 py-3 text-slate-600">v{sub.version || 1}</td>
                      <td className="px-6 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-slate-500">
                        {formatDate(sub.created)}
                      </td>
                      <td className="px-6 py-3">
                        <button
                          onClick={() => handleDownload(sub)}
                          className="text-indigo-600 hover:text-indigo-800 transition"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── StatCard ──────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${colors[color] || 'bg-slate-50 text-slate-600'}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

export default DesignerSubmissions;